import { ImageName } from "@mcleod/images";
import {DatePart, DateUtil, ModelRow} from "@mcleod/core";
import { ClickEvent, Image, ImageProps, Panel, PanelProps, Textbox } from "@mcleod/components";
import {AutogenLayoutMovementCard} from "./autogen/AutogenLayoutMovementCard";
import {getDispatchControlValue} from "@mcleod/general";
import { DetentionItems, PlanningUtil } from "../PlanningUtil";

export class MovementCard extends AutogenLayoutMovementCard {
    data: ModelRow;

    onLoad(): void | Promise<void> {
        this.panelCollapsed.style.overflow = "hidden";
        this.textboxTrailer.printableLabel.wrap = false;
        this.textboxTrailer2.printableLabel.wrap = false;
        this.textboxTrailer3.printableLabel.wrap = false;
        this.textboxDolly1.printableLabel.wrap = false;
        this.textboxDolly2.printableLabel.wrap = false;
    }

    displayData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        super.displayData(data, allData, rowIndex);
        this.displayStatus();
        this.displayTimezones();
    }

    displayTimezones() {
        this.addTimeZone("textboxScheduledPickup", "origin_timezone");
        this.addTimeZone("labelOriginLate", "origin_timezone");
        this.addTimeZone("textboxOriginArrival", "origin_timezone");
        this.addTimeZone("textboxOriginDeparture", "origin_timezone");
        this.addTimeZone("textboxScheduledDelivery", "destination_timezone");
        this.addTimeZone("labelDestLate", "destination_timezone");
        this.addTimeZone("textboxDestinationArrival", "destination_timezone");
        this.addTimeZone("textboxDestinationDeparture", "destination_timezone");
    }

    displayStatus(arrived: boolean = false, isFinalStop: boolean = false) {
        const moveStatus = this.data.get("move_status");
        let collapsedStatusProps: Partial<PanelProps>;
        let imageStatusProps: Partial<ImageProps>;
        let panelTopRowProps: Partial<PanelProps>;
        let panelBottomProps: Partial<PanelProps>;
        let captionColor: string;

        const detentionItems: DetentionItems = {
            origin: {
                image: this.imageOriginDetention,
                field: "origin_detention",
            },
            destination: {
                image: this.imageDestinationDetention,
                field: "destination_detention",
            },
            statuses: {
                image: [
                    this.imageDetentionActive,
                    this.imageDetentionReview,
                    this.imageDetentionClosed,
                ],
                field: "detention_statuses"
            }
        };

        switch (moveStatus) {
            case "P": {
                if (arrived) {
                    imageStatusProps = { name: ImageName.loadPickup, tooltip: "In Progress" };
                    panelTopRowProps = { backgroundColor: "warning.darker", color: "default.reverse" };
                    panelBottomProps = { backgroundColor: "warning", color: "default.reverse" };
                } else {
                    imageStatusProps = { name: ImageName.loadInProgress, tooltip: "In Progress" };
                    panelTopRowProps = { backgroundColor: "primary.darker", color: "default.reverse" };
                    panelBottomProps = { backgroundColor: "primary.dark", color: "default.reverse" };
                }
                this.setDetentionColors(detentionItems, "default.reverse", "default.reverse");
                collapsedStatusProps = panelTopRowProps;
                captionColor = "default.reverse";
                break;
            }
            case "D": {
                collapsedStatusProps = panelTopRowProps  = { backgroundColor: "#F2E6F2", color: "#800080" };
                imageStatusProps = { name: ImageName.loadDelivery, tooltip: "Delivered" };
                panelBottomProps = { backgroundColor: "#663399", color: "default.reverse" };
                this.setDetentionColors(detentionItems, "default.reverse", "#800080");
                captionColor = "#800080";
                break;
            }
            case "A": {
                if (arrived) {
                    panelTopRowProps = { backgroundColor: "warning.darker", color: "default.reverse" };
                    panelBottomProps = { backgroundColor: "warning", color: "default.reverse" };
                    this.setDetentionColors(detentionItems, "default.reverse", "default.reverse");
                    collapsedStatusProps = panelTopRowProps;
                } else {
                    const pickupWarningTimeHours: number = getDispatchControlValue("pickup_warning");
                    const pickupWarningTimeMin: number = pickupWarningTimeHours * 60;
                    const originLate = this.data.getDateTime("origin_late", null);
                    const originEarly = this.data.getDateTime("origin_early", null);
                    const appointmentTime: Date = originLate && originLate?.getDate() ? originLate : originEarly;
                    const minTillPickup = DateUtil.dateDiff(DatePart.MINUTE, new Date(appointmentTime.setSeconds(0, 0)), new Date(new Date().setSeconds(0, 0)));
                    if (minTillPickup < 0) {
                        // Past pickup time (ERROR)
                        collapsedStatusProps = { backgroundColor: "error", color: "error.reverse" };
                    } else if (minTillPickup <= pickupWarningTimeMin) {
                        // Within warning time (WARN)
                        collapsedStatusProps = { backgroundColor: "caution", color: "caution.darkest" };
                    } else {
                        // Outside warning time (OK)
                        collapsedStatusProps = { backgroundColor: "#F1F6EE", color: "success.darker" };
                    }
                    panelTopRowProps = { backgroundColor: "#F1F6EE", color: "success.darker" };
                    panelBottomProps = { backgroundColor: "success", color: "default.reverse" };
                    this.setDetentionColors(detentionItems, "default.reverse", "success.darker");
                    this.panelScheduledPickup.backgroundColor = minTillPickup < 0 ? "error" : minTillPickup <= pickupWarningTimeMin ? "#C68E00" : "success";
                }
                captionColor = arrived ? "default.reverse" : "success";
                imageStatusProps = { name: ImageName.loadCovered, tooltip: "Available" };
                break;
            }
        }

        if (!this.data.getBoolean("loaded")) {
            collapsedStatusProps = panelTopRowProps = { backgroundColor: "subtle.lightest", color: "subtle.darkest" };
            panelBottomProps = { backgroundColor: "subtle", color: "default.reverse" };
            imageStatusProps = { name: ImageName.emptyMove, tooltip: "Empty" };
            this.setDetentionColors(detentionItems, "default.reverse", "default.reverse");
            captionColor = "subtle.darker";
        }

        this.collapsedStatus.setProps(imageStatusProps);
        this.imageStatus.setProps(imageStatusProps);
        this.panelCollapsed.setProps(collapsedStatusProps);
        this.panelMoveHeader.setProps(panelTopRowProps);
        this.panelMoveHeader.element.style.justifyContent = "center";
        this.panelTopRow.setProps(panelTopRowProps);
        this.panelTopRow.findComponentsByType(Textbox).forEach(tb => tb.captionProps = { color: captionColor });
        this.panelBottomRow.setProps(panelBottomProps);
        this.panelBottomRow.findComponentsByType(Textbox).forEach(tb => tb.captionProps = { color: "default.reverse" });
        this.displayPickupAndDelivery(moveStatus, arrived, isFinalStop);
        PlanningUtil.displayDetentionIcon(this.data, detentionItems)
    }

    setDetentionColors(detentionItems: DetentionItems, originDest: string, intermediate: string) {
        detentionItems.origin.color = originDest;
        detentionItems.destination.color = originDest;
        detentionItems.statuses.color = intermediate;
    }

    displayPickupAndDelivery(status: string, arrived: boolean, isFinalStop: boolean) {
        if (status == "P" || status == "D") {
            this.panelScheduledPickup.visible = false;
            this.panelScheduledDelivery.visible = status != "D" && (!arrived || (arrived && !isFinalStop));
            this.panelActualOrigin.visible = true;
            this.panelActualDelivery.visible = status == "D" || (arrived && isFinalStop);
        } else if (status == "A" && arrived) {
            this.panelScheduledPickup.visible = false;
            this.panelActualOrigin.visible = true;
        }
    }

    private addTimeZone(dateComponentId: string, timeZoneField: string) {
        const dateComponent = this.findComponentById(dateComponentId) as Textbox;
        if(dateComponent.text !== "--") {
            dateComponent.text = dateComponent.text +  " " + this.data.get(timeZoneField);
        }
    }


    get expandedHeight(): string|number {
        return this.panelMovement.height;
    }

    get expandedWidth(): string|number {
        return this.panelMovement.width;
    }

    get startDate(): Date {
        return this.data.getDateTime("start_date");
    }

    get endDate(): Date {
        return this.data.getDateTime("end_date");
    }

    get collapsedPanel(): Panel {
        return this.panelCollapsed;
    }

    get overlapSpacer(): Panel {
        return this.panelOverlapSpacer;
    }

    cellStopsOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Image, true, this.data.get("dispatch_id", null));
    }
}
